import { useState, useEffect, useRef } from 'react'
import { supabase } from '../supabase'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const ANON_KEY    = import.meta.env.VITE_SUPABASE_ANON_KEY

export default function TwoFactorPage({ email, onVerified, onLogout }) {
  const [code,      setCode]      = useState(['', '', '', '', '', ''])
  const [loading,   setLoading]   = useState(false)
  const [sending,   setSending]   = useState(false)
  const [error,     setError]     = useState('')
  const [countdown, setCountdown] = useState(0)
  const [verified,  setVerified]  = useState(false)
  const inputs = useRef([])

  // Envoyer l'OTP au montage
  useEffect(() => { envoyerOTP() }, [])

  // Décompte de renvoi
  useEffect(() => {
    if (countdown <= 0) return
    const t = setTimeout(() => setCountdown(c => c - 1), 1000)
    return () => clearTimeout(t)
  }, [countdown])

  const envoyerOTP = async () => {
    setSending(true); setError('')
    try {
      await supabase.auth.signOut()
      const resp = await fetch(`${SUPABASE_URL}/auth/v1/otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': ANON_KEY },
        body: JSON.stringify({ email, create_user: false }),
      })
      const body = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        setError(body.msg || body.message || 'Erreur lors de l\'envoi du code.')
      } else {
        setCountdown(60)
      }
    } catch (e) { console.error('[2FA] Erreur envoi OTP:', e); setError('Erreur réseau.') }
    finally { setSending(false) }
  }

  const handleInput = (i, val) => {
    const v = val.replace(/\D/g, '').slice(-1)
    const next = [...code]; next[i] = v; setCode(next)
    setError('')
    if (v && i < 5) inputs.current[i + 1]?.focus()
    if (!v && i > 0) inputs.current[i - 1]?.focus()
  }

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pasted.length === 6) {
      setCode(pasted.split(''))
      inputs.current[5]?.focus()
    }
    e.preventDefault()
  }

  const handleVerify = async () => {
    const token = code.join('')
    if (token.length < 6) { setError('Entrez les 6 chiffres du code.'); return }
    setLoading(true); setError('')
    try {
      const resp = await fetch(`${SUPABASE_URL}/auth/v1/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': ANON_KEY },
        body: JSON.stringify({ type: 'email', email, token }),
      })
      const body = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        setError(body.msg || body.message || 'Code incorrect ou expiré.')
        setCode(['', '', '', '', '', ''])
        inputs.current[0]?.focus()
      } else {
        setVerified(true)
        onVerified()
        if (body.access_token && body.refresh_token) {
          await supabase.auth.setSession({
            access_token:  body.access_token,
            refresh_token: body.refresh_token,
          })
        }
      }
    } catch {
      setError('Erreur réseau lors de la vérification.')
    }
    finally { setLoading(false) }
  }

  const codeComplet = code.every(c => c !== '')

  return (
    <div style={{
      minHeight: '100vh', background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Bricolage Grotesque', sans-serif", padding: '1rem',
    }}>
      <div style={{
        background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.1)', borderRadius: 24,
        padding: '3rem 2.5rem', width: '100%', maxWidth: 420,
        boxShadow: '0 25px 80px rgba(0,0,0,0.5)', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4,
          background: 'linear-gradient(90deg, #FF6B35, #00D4AA, #FF6B35)', backgroundSize: '200% 100%' }} />

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 800, letterSpacing: -1,
            background: 'linear-gradient(135deg, #FF6B35, #00D4AA)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '0.5rem' }}>
            ☀️ SOLSTICE
          </div>
          <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🔐</div>
          <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', fontWeight: 800, color: 'white' }}>
            Vérification en deux étapes
          </h2>
          <p style={{ margin: 0, color: '#64748B', fontSize: '0.85rem', lineHeight: 1.5 }}>
            Un code à 6 chiffres a été envoyé à<br />
            <strong style={{ color: '#94A3B8' }}>{email}</strong>
          </p>
        </div>

        {/* Écran de succès */}
        {verified && (
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>✅</div>
            <p style={{ margin: 0, color: '#10B981', fontWeight: 700, fontSize: '1rem' }}>Code validé !</p>
            <p style={{ margin: '0.5rem 0 0', color: '#64748B', fontSize: '0.85rem' }}>Connexion en cours…</p>
          </div>
        )}

        {/* Contenu OTP — masqué après validation */}
        {!verified && (
          <>
            {/* Champs OTP */}
            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'center', marginBottom: '1.5rem' }}>
              {code.map((digit, i) => (
                <input
                  key={i}
                  ref={el => inputs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  autoFocus={i === 0}
                  onPaste={i === 0 ? handlePaste : undefined}
                  onChange={e => handleInput(i, e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Backspace' && !code[i] && i > 0) inputs.current[i - 1]?.focus()
                    if (e.key === 'Enter' && codeComplet) handleVerify()
                  }}
                  style={{
                    width: 48, height: 56, textAlign: 'center', fontSize: '1.4rem',
                    fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
                    borderRadius: 12, outline: 'none',
                    border: `2px solid ${digit ? 'rgba(0,212,170,0.6)' : 'rgba(255,255,255,0.12)'}`,
                    background: digit ? 'rgba(0,212,170,0.08)' : 'rgba(255,255,255,0.04)',
                    color: 'white', transition: 'all 0.15s ease',
                  }}
                />
              ))}
            </div>

            {/* Erreur */}
            {error && (
              <p style={{ margin: '0 0 1rem', color: '#FCA5A5', fontSize: '0.85rem', textAlign: 'center' }}>
                ⚠️ {error}
              </p>
            )}

            {/* Bouton vérifier */}
            <button onClick={handleVerify} disabled={loading || !codeComplet}
              style={{
                width: '100%', padding: '0.9rem',
                background: loading || !codeComplet ? 'rgba(255,107,53,0.2)' : 'linear-gradient(135deg,#FF6B35,#E55A2B)',
                border: 'none', borderRadius: 12,
                color: loading || !codeComplet ? '#64748B' : 'white',
                fontWeight: 700, fontSize: '1rem',
                cursor: loading || !codeComplet ? 'not-allowed' : 'pointer',
                marginBottom: '1rem',
              }}>
              {loading ? '⏳ Vérification…' : '✅ Valider le code'}
            </button>

            {/* Renvoi */}
            <div style={{ textAlign: 'center' }}>
              {countdown > 0 ? (
                <p style={{ margin: 0, color: '#475569', fontSize: '0.82rem' }}>
                  Renvoyer un code dans <strong style={{ color: '#64748B' }}>{countdown}s</strong>
                </p>
              ) : (
                <button onClick={envoyerOTP} disabled={sending}
                  style={{ background: 'none', border: 'none', color: '#00D4AA',
                    cursor: sending ? 'not-allowed' : 'pointer', fontSize: '0.82rem',
                    fontWeight: 600, textDecoration: 'underline' }}>
                  {sending ? 'Envoi…' : '📧 Renvoyer un code'}
                </button>
              )}
            </div>

            {/* Déconnexion */}
            <div style={{ textAlign: 'center', marginTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '1rem' }}>
              <button onClick={onLogout}
                style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '0.78rem' }}>
                ← Retour à la connexion
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}